import { useState } from 'react'
import { signIn, signOut, NO_ACCESS } from '../lib/auth'
import * as api from '../lib/api'

/**
 * Sign in.
 *
 * The same account as every other Binny product — lereco validates a token, it
 * never issues user records. There is no sign-up and no password reset here on
 * purpose: accounts come from Vision, and a reset link on an internal console is
 * a phishing surface with no upside.
 *
 * This form used to sign anyone in on submit, which was honest while there was
 * no backend to protect. There is one now, and `/api/v1/ledger` refuses anything
 * without a valid Binny session.
 */
export default function Login({ onSignIn, notice = '' }) {
  const [showPw, setShowPw] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [busy, setBusy] = useState(false)

  /*
   * Three error slots, not one.
   *
   * The shared auth endpoint says which half was wrong, and putting that under
   * the field it applies to is the difference between "try again" and knowing
   * what to try. `error` is the banner for everything that is neither — a
   * service that is down, a console that is not configured.
   */
  const [error, setError] = useState(notice)
  const [userErr, setUserErr] = useState('')
  const [pwErr, setPwErr] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setUserErr('')
    setPwErr('')

    // An empty field is the same class of problem as a wrong one, so it gets the
    // same per-field message rather than a banner.
    let bad = false
    if (!username.trim()) {
      setUserErr('Enter your username')
      bad = true
    }
    if (!password) {
      setPwErr('Enter your password')
      bad = true
    }
    if (bad) return

    setBusy(true)
    try {
      const user = await signIn({ username: username.trim(), password, rememberMe })
      // Only admins and the accounts team get in. Anyone else's Binny session
      // is dropped on the spot, so they never get past this form.
      let me
      try {
        me = await api.getMe()
      } catch (e) {
        signOut()
        if (e?.code === 'NOT_A_LERECO_USER') {
          setError(NO_ACCESS)
          return
        }
        throw e
      }
      onSignIn(user, me)
    } catch (e) {
      // Vision's own wording, mapped onto the field it belongs to. The same
      // strings the other consoles match on — kept in step deliberately, so a
      // change upstream shows up in one grep rather than four.
      const msg = String(e.message || '')
      if (/user (does not exist|not found)|no such user/i.test(msg)) setUserErr('Invalid username')
      else if (/invalid (user )?credentials|incorrect password|invalid password|wrong password/i.test(msg))
        setPwErr('Invalid password')
      else setError(msg || 'Invalid username or password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="login-mk">
          lereco<span>.</span>
        </div>
        <div className="login-tag">Ledger Reconciliation</div>

        <h1 className="login-h1">Sign in</h1>
        <p className="login-lead">Your Binny account. The same login you use for the other consoles.</p>

        {error && (
          <div className="note" style={{ borderColor: 'var(--alert)', marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div className="login-field">
          <label htmlFor="lg-user">Username</label>
          <input
            id="lg-user"
            type="text"
            autoComplete="username"
            placeholder="you@binnys.in"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={busy}
            autoFocus
          />
          {userErr && <span className="c-sub" style={{ color: 'var(--alert)' }}>{userErr}</span>}
        </div>

        <div className="login-field">
          <label htmlFor="lg-pw">Password</label>
          <div className="pwwrap">
            <input
              id="lg-pw"
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            <span className="eye" onClick={() => setShowPw((v) => !v)} style={{ '--x': 0 }}>
              {/* The mockup drove this label from CSS ::after; with state it can
                  just be text, which also makes it readable to a screen reader. */}
              <span style={{ position: 'relative', zIndex: 1 }}>{showPw ? 'hide' : 'show'}</span>
            </span>
          </div>
          {pwErr && <span className="c-sub" style={{ color: 'var(--alert)' }}>{pwErr}</span>}
        </div>

        <div className="login-remember">
          <span>Keep me signed in</span>
          <label>
            <input
              className="ctl tg"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={busy}
            />
            <span className="sw" />
          </label>
        </div>

        <button className="login-btn" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="login-restrict">Internal use only · Binny Jewellers</div>
      </form>
    </div>
  )
}
